import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import {
  ProgramacionMasiva,
  ProgramacionMasivaDetalle,
  CrearProgramacionMasivaRequest,
  BuscarAsesoresRequest,
  AgregarAsesorRequest,
  AgregarAsesoresRequest
} from 'src/app/domain/model/agenda/programacion-masiva';

/**
 * Adaptador HTTP: HttpProgramacionMasivaRepository
 *
 * Servicio para comunicación HTTP con backend de Programación Masiva.
 * Endpoints: /api/agenda/programacion-masiva/*
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpProgramacionMasivaRepository {

  private readonly http = inject(HttpClient);
  private readonly usuarioState = inject(UsuarioStateService);
  private readonly baseUrl = `${environment.backend.baseUrl}/agenda/programacion-masiva`;

  /**
   * Obtiene headers con DNI de usuario autenticado.
   */
  private getHeadersWithUserDni(dniUsuario: string): HttpHeaders {
    return new HttpHeaders({ 'X-User-DNI': dniUsuario });
  }



  // === PROGRAMACIÓN MASIVA ===

  /**
   * Crea una nueva programación masiva
   */
  async crear(request: CrearProgramacionMasivaRequest): Promise<ProgramacionMasiva> {
    const dniUsuario = await this.usuarioState.obtenerDni();
    return firstValueFrom(
      this.http.post<ProgramacionMasiva>(
        this.baseUrl,
        request,
        { headers: this.getHeadersWithUserDni(dniUsuario) }
      )
    );
  }

  /**
   * Obtiene una programación masiva por ID
   */
  async obtenerPorId(id: number): Promise<ProgramacionMasiva> {
    return firstValueFrom(
      this.http.get<ProgramacionMasiva>(`${this.baseUrl}/${id}`)
    );
  }

  // === ASESORES ===

  /**
   * Busca asesores disponibles según tipos y oficina
   */
  async buscarAsesores(request: BuscarAsesoresRequest): Promise<ProgramacionMasivaDetalle[]> {
    return firstValueFrom(
      this.http.post<ProgramacionMasivaDetalle[]>(
        `${this.baseUrl}/buscar-asesores`,
        request
      )
    );
  }

  /**
   * Lista asesores asignados a una programación
   */
  async listarAsesores(programacionId: number): Promise<ProgramacionMasivaDetalle[]> {
    return firstValueFrom(
      this.http.get<ProgramacionMasivaDetalle[]>(
        `${this.baseUrl}/${programacionId}/asesores`
      )
    );
  }

  /**
   * Agrega un asesor a la programación
   */
  async agregarAsesor(
    programacionId: number,
    request: AgregarAsesorRequest
  ): Promise<ProgramacionMasivaDetalle> {
    const dniUsuario = await this.usuarioState.obtenerDni();
    return firstValueFrom(
      this.http.post<ProgramacionMasivaDetalle>(
        `${this.baseUrl}/${programacionId}/asesores`,
        request,
        { headers: this.getHeadersWithUserDni(dniUsuario) }
      )
    );
  }

  /**
   * Agrega múltiples asesores a la programación EN LOTE
   * (Como el legacy: agregarAsesoresProgramacion)
   */
  async agregarAsesoresEnLote(
    programacionId: number,
    request: AgregarAsesoresRequest
  ): Promise<ProgramacionMasivaDetalle[]> {
    const dniUsuario = await this.usuarioState.obtenerDni();
    return firstValueFrom(
      this.http.post<ProgramacionMasivaDetalle[]>(
        `${this.baseUrl}/${programacionId}/asesores/lote`,
        request,
        { headers: this.getHeadersWithUserDni(dniUsuario) }
      )
    );
  }

  /**
   * Elimina un asesor de la programación
   */
  async eliminarAsesor(programacionId: number, detalleId: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${this.baseUrl}/${programacionId}/asesores/${detalleId}`
      )
    );
  }

  // === EJECUCIÓN ===

  /**
   * Ejecuta la programación (crea citas para todos los asesores)
   *
   * Legacy: Los campos condicionales (beneficiario, tarea, centroTrabajo) NO se guardan
   * en la tabla TPSC_PROGRAMACION_MASIVA. Se pasan directamente al SP.
   */
  async ejecutar(
    programacionId: number,
    camposCondicionales?: {
      nmconsecutivoBenef?: number;
      nmconsecBenefCt?: number;
      cdtarea?: number;
    }
  ): Promise<ProgramacionMasiva> {
    const dniUsuario = await this.usuarioState.obtenerDni();
    return firstValueFrom(
      this.http.post<ProgramacionMasiva>(
        `${this.baseUrl}/${programacionId}/ejecutar`,
        camposCondicionales || {},
        { headers: this.getHeadersWithUserDni(dniUsuario) }
      )
    );
  }

  /**
   * Lista errores de la programación
   */
  async listarErrores(programacionId: number): Promise<ProgramacionMasivaDetalle[]> {
    return firstValueFrom(
      this.http.get<ProgramacionMasivaDetalle[]>(
        `${this.baseUrl}/${programacionId}/errores`
      )
    );
  }
}
